import first from "lodash/first";
import last from "lodash/last";
import { graphql, readInlineData } from "react-relay";

import { article as articleData, section as sectionData } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import type { helpersCheckAnnouncementArticle$key } from "scmp-app/queries/__generated__/helpersCheckAnnouncementArticle.graphql";
import type { helpersCheckArticleIsInSectionsArticle$key } from "scmp-app/queries/__generated__/helpersCheckArticleIsInSectionsArticle.graphql";
import type { helpersCheckArticleRenderTypeArticle$key } from "scmp-app/queries/__generated__/helpersCheckArticleRenderTypeArticle.graphql";
import type { helpersCheckHasLiveTagArticle$key } from "scmp-app/queries/__generated__/helpersCheckHasLiveTagArticle.graphql";
import type { helpersCheckIsVideoContent$key } from "scmp-app/queries/__generated__/helpersCheckIsVideoContent.graphql";
import type { helpersGetArticleTypeArticle$key } from "scmp-app/queries/__generated__/helpersGetArticleTypeArticle.graphql";

import {
  checkCanShowInPlusPlatform,
  checkIsContainsPlusQueryString,
  checkIsPlusArticle,
} from "./plus/helpers";
import { checkIsPostMagazineArticle } from "./post-magazine-article/helper";
import { checkIsStyleArticle } from "./style-article/helper";
import { ArticleRenderType } from "./types";

export const getArticleType = (reference: helpersGetArticleTypeArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersGetArticleTypeArticle on Content @inline {
        ... on Article {
          types {
            value {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );
  const [articleType] = article.types?.flatMap(type => type?.value) ?? [];
  return articleType?.entityId;
};

export const checkArticleIsInSections = (
  reference: helpersCheckArticleIsInSectionsArticle$key,
  entityIds: string[],
  options?: { isCheckArticleFirstSectionOnly?: boolean },
) => {
  const { isCheckArticleFirstSectionOnly = false } = options ?? {};
  const article = readInlineData(
    graphql`
      fragment helpersCheckArticleIsInSectionsArticle on Content @inline {
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );

  const targetArticleSections = isCheckArticleFirstSectionOnly
    ? article?.sectionsV2?.slice(0, 1)
    : article?.sectionsV2;
  const flattenArticleSectionSegments =
    targetArticleSections?.flatMap(
      section => section?.value?.map(value => value && value.entityId).filter(notEmpty) ?? [],
    ) ?? [];
  return entityIds.some(entityId => flattenArticleSectionSegments.includes(entityId));
};

export const checkHasLiveTag = (reference: helpersCheckHasLiveTagArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckHasLiveTagArticle on Article @inline {
        flattenTypeIds
        liveArticle {
          status
        }
      }
    `,
    reference,
  );
  const isLiveArticle =
    article.flattenTypeIds?.includes(articleData.types.live.entityUuid) ?? false;
  const isLive = article.liveArticle?.status ?? false;
  return isLiveArticle && isLive;
};

export const checkIsVideoContent = (reference: helpersCheckIsVideoContent$key) => {
  const content = readInlineData(
    graphql`
      fragment helpersCheckIsVideoContent on Content @inline {
        __typename
      }
    `,
    reference,
  );
  return content.__typename === "Video";
};

export const checkArticleRenderType = (
  reference: helpersCheckArticleRenderTypeArticle$key,
  query: Record<string, string | string[] | undefined>,
) => {
  const content = readInlineData(
    graphql`
      fragment helpersCheckArticleRenderTypeArticle on Content @inline {
        ...helpersCheckCanShowInPlusPlatformContent
        ...helpersCheckIsPlusArticle
        ...helpersCheckIsPostMagazineArticle
        ...helpersCheckIsStyleArticle
        ...helpersCheckIsVideoContent
        ...helpersGetArticleTypeArticle
      }
    `,
    reference,
  );

  if (checkIsVideoContent(content)) return ArticleRenderType.Video;
  if (checkIsStyleArticle(content)) return ArticleRenderType.Style;
  else if (
    checkIsPlusArticle(content) ||
    (checkCanShowInPlusPlatform(content) && checkIsContainsPlusQueryString(query))
  )
    return ArticleRenderType.Plus;
  else if (checkIsPostMagazineArticle(content)) return ArticleRenderType.PostMagazine;

  const articleType = getArticleType(content);

  switch (articleType) {
    case articleData.types.infographic.entityId:
      return ArticleRenderType.Infographic;
    case articleData.types.infographicLongRead.entityId:
      return ArticleRenderType.InfographicLongRead;
    case articleData.types.live.entityId:
      return ArticleRenderType.Live;
    case articleData.types.longRead.entityId:
      return ArticleRenderType.LongRead;
    case articleData.types.newsletter.entityId:
      return ArticleRenderType.Newsletter;
    case articleData.types.photoEssay.entityId:
      return ArticleRenderType.PhotoEssay;
    case articleData.types.series.entityId:
      return ArticleRenderType.Series;
    case articleData.types.studio.entityId:
      return ArticleRenderType.MorningStudio;
    default:
      return ArticleRenderType.Generic;
  }
};

export const checkIsAnnouncementArticle = (reference: helpersCheckAnnouncementArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersCheckAnnouncementArticle on Article @inline {
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
          }
        }
      }
    `,
    reference,
  );
  const mainSection = last(first(article.sectionsV2)?.value);
  return mainSection?.entityId === sectionData.announcements.entityId;
};
